import React, { useState, useRef, Fragment } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, TagIcon, ChevronDownIcon, ExternalLinkIcon, NewspaperIcon, TrophyIcon, InstagramIcon, FacebookIcon, PlayCircleIcon, XIcon, Share2Icon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost, SPORTS_COMPOSITE_IMAGES } from '../data/blog-posts';
import { getCategoryColor, getPostNum, resolveTranslatedField, translateCategory } from '../utils/blog-helpers';
import { StoryBody, stripImageMarkers } from './shared/StoryBody';
// ============================================================
//  Blog posts data is now in data/blog-posts.ts
//  Edit that file to add or modify blog posts.
// ============================================================
const ALL_CATEGORIES = ['All', 'Political', 'Immigration', 'Business', 'Note Worthy', 'Events', 'Arts & Entertainment', 'Sports'];
const GRADIENT_FALLBACKS: Record<string, string> = {
  Political: 'from-haa-blue via-blue-700 to-haa-navy',
  Immigration: 'from-haa-skyblue to-haa-midblue',
  Business: 'from-emerald-500 to-emerald-800',
  'Note Worthy': 'from-haa-midblue to-haa-navy',
  'Arts & Entertainment': 'from-haa-navy to-blue-950',
  Events: 'from-blue-400 to-haa-midblue',
  Sports: 'from-orange-500 to-red-600'
};
function getGradient(category: string) {
  return GRADIENT_FALLBACKS[category] ?? 'from-haa-blue to-haa-navy';
}
// Story Modal Component
function StoryModal({
  isOpen,
  onClose,
  post,
  translatedTitle,
  translatedDate,
  translatedFullStory,
  isEs,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost;
  translatedTitle: string;
  translatedDate: string;
  translatedFullStory?: string;
  isEs: boolean;
  t: any;
}) {
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  return <AnimatePresence data-id="element-3098" data-mp="37n81ii0f6ej2">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-3099" data-mp="3fofltzaiu4s5">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-3100" data-mp="2w0y5rgmz13sj">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-3101" data-mp="2amwkr01p4926">
              <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3102" data-mp="20qjru4dz5z8h">
                {post.category}
              </span>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-3103" data-mp="1rr6ev1qn6sj0">
                <XIcon className="w-5 h-5" data-id="element-3104" data-mp="1hyh8gl9y84uj" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" data-id="element-3105" data-mp="1cznuysunwdud">
              {post.image && !(post.players && post.players.length > 0) && <div className="w-full h-80 sm:h-96 relative" data-id="element-3106" data-mp="140al3z2mln94">
                  <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover" style={{
              objectPosition: post.imagePosition || 'center top'
            }} data-id="element-3107" data-mp="1436gpgr03zue" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-3108" data-mp="1o0qbtgcqrfj8" />
                </div>}
              {post.image && post.imageCaption && <p className="px-6 sm:px-8 pt-3 font-nunito text-sm italic text-gray-600 leading-relaxed" data-id="element-3109" data-mp="1a3bja2r6yrn1">
                  {isEs && post.imageCaptionEs ? post.imageCaptionEs : post.imageCaption}
                </p>}

              {/* Video Embeds — shown at top when no image */}
              {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="w-full" data-id="element-3110" data-mp="2zevf0754rldi">
                    {post.videoEmbeds.map((video, vi) => <div key={vi} className="bg-black relative" data-id="element-3111" data-mp="ntcwr1lnz3rh" data-mpi={vi}>
                        {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-3112" data-mp="jssy1tlfzs38">
                            <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-3113" data-mp="17gait56ahpwp" />
                            {/* Fallback thumbnail link */}
                            <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-3114" data-mp="1r4safn1g5w9x">
                              <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-3115" data-mp="2528hn0rkhu2q" />
                              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-3116" data-mp="2itn45hkoodej">
                                <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-3117" data-mp="c77lvi2cbbtm" />
                                <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-3118" data-mp="78ml9hj6eq4n">
                                  Watch on YouTube
                                </span>
                              </div>
                            </a>
                          </div> : null}
                      </div>)}
                  </div>}

              <div className="p-6 sm:p-8" data-id="element-3119" data-mp="qwvk2af85ud4">
                <div className="flex items-center gap-2 mb-4" data-id="element-3120" data-mp="m5n8b7qf9wws">
                  <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-3121" data-mp="m3sqw3ed0mxg" />
                  <span className="font-nunito text-gray-500 text-sm font-semibold" data-id="element-3122" data-mp="h5hgnrk5kaep">
                    {translatedDate}
                  </span>
                </div>

                <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-6" data-id="element-3123" data-mp="19vhnvx24lzkq">
                  {translatedTitle}
                </h2>

                {translatedFullStory && <StoryBody text={translatedFullStory} className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-8" data-id="element-3124" data-mp="myv5zg45psu8" />}

                {/* Video Embeds — shown after story when post has image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8 space-y-4" data-id="element-3125" data-mp="15wfzj0hv9fl6">
                      {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black relative" data-id="element-3126" data-mp="rzj1of3byxus" data-mpi={vi}>
                          {video.youtubeId ? <div className="relative w-full" style={{
                  paddingBottom: '56.25%'
                }} data-id="element-3127" data-mp="3gps9djhkr13t">
                              <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                    border: 0
                  }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-3128" data-mp="346euev9p7s3b" />
                              <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-3129" data-mp="22qgxntdz2lws">
                                <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-3130" data-mp="26lge8q8l9ab4" />
                                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-3131" data-mp="1ssqp4cxrjp9s">
                                  <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-3132" data-mp="1nw0ei54q9lvr" />
                                  <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-3133" data-mp="1iukrw8mwj7b9">
                                    Watch on YouTube
                                  </span>
                                </div>
                              </a>
                            </div> : null}
                        </div>)}
                    </div>}

                {/* Story Images Gallery */}
                {post.storyImages && post.storyImages.length > 0 && <div className="mb-8 space-y-4" data-id="element-3134" data-mp="l35x4m0yaqbn">
                    {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-3135" data-mp="q1fy5d9cwl56" data-mpi={idx}>
                        <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-3136" data-mp="zr933vezu20g" />
                      </div>)}
                  </div>}

                {/* Players */}
                {post.players && post.players.length > 0 && <div className="space-y-3 mb-8" data-id="element-3137" data-mp="3qgti4zgxyzsv">
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-3138" data-mp="3qe7e39t9f2fe">
                      {isEs ? 'Jugadores Destacados' : 'Featured Players'}
                    </h3>
                    {post.players.map(player => {
                const isExpanded = expandedPlayer === player.name;
                const playerKeyMap: Record<string, string> = {
                  'Oscar Avilez': 'avilez',
                  'David Ruíz': 'ruiz',
                  'Joseph Rosales': 'rosales',
                  'Andy Nájar': 'najar',
                  'Bryan Acosta': 'acosta'
                };
                const playerKey = playerKeyMap[player.name] ?? '';
                const translatedBio = playerKey ? t(`news.player.${playerKey}.bio`) : player.bio;
                const translatedPosition = player.position === 'Midfielder' ? t('news.player.position.midfielder') : player.position === 'Defender' ? t('news.player.position.defender') : player.position;
                return <div key={player.name} className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" data-id="element-3139" data-mp="1n06i0yyamvl1">
                          <button onClick={() => setExpandedPlayer(isExpanded ? null : player.name)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors text-left" aria-expanded={isExpanded} data-id="element-3140" data-mp="1e5s53318jx53">
                            {player.image ? <img src={player.image} alt={player.name} className="w-16 h-16 rounded-full object-cover object-top shadow-md flex-shrink-0 border-2 border-white" data-id="element-3141" data-mp="my1n25skjl6x" /> : <div className={`w-16 h-16 rounded-full ${player.teamColor} flex items-center justify-center text-white font-nunito font-bold text-lg shadow-md flex-shrink-0`} data-id="element-3142" data-mp="15r5i9vlfq63n">
                                {player.name.split(' ').map(n => n[0]).join('')}
                              </div>}
                            <div className="flex-1 min-w-0" data-id="element-3143" data-mp="1jpm12kwknn25">
                              <p className="font-nunito text-sm font-bold text-haa-navy leading-tight" data-id="element-3144" data-mp="32deukd4pi5z0">
                                {player.name}
                                <span className="text-gray-400 font-normal ml-1.5" data-id="element-3145" data-mp="37daykcdelxek">
                                  #{player.number}
                                </span>
                              </p>
                              <p className="font-nunito text-xs text-gray-500 mt-0.5" data-id="element-3146" data-mp="3ger7sgshfth7">
                                {translatedPosition} · {player.team}
                              </p>
                            </div>
                            <motion.span animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-3147" data-mp="3q99i44semn9z">
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-3148" data-mp="cwqwosrmr7zd" />
                            </motion.span>
                          </button>
                          <AnimatePresence initial={false} data-id="element-3149" data-mp="241aoaa3eh6lh">
                            {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-3150" data-mp="2iqgtceox5r1a">
                                <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-3151" data-mp="3e0lskzibmauy">
                                  <p className="font-nunito text-sm text-gray-600 leading-relaxed" data-id="element-3152" data-mp="3iwu1hv5xd7to">
                                    {translatedBio}
                                  </p>
                                  <a href={player.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue transition-colors" data-id="element-3153" data-mp="3967rqhkw3biw">
                                    {t('news.player.viewProfile')}
                                    <ExternalLinkIcon className="w-3 h-3" data-id="element-3154" data-mp="jjjjxpc9dotl" />
                                  </a>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>}

                {/* Video Embeds — shown after story when there is an image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8" data-id="element-3155" data-mp="2opv8x9bdlx0k">
                      <div className="flex items-center gap-2 mb-4" data-id="element-3156" data-mp="2oqy0ljgiw3hj">
                        <PlayCircleIcon className="w-5 h-5 text-amber-600" data-id="element-3157" data-mp="2kmyore66tsui" />
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider" data-id="element-3158" data-mp="2foeyx2oh3wwv">
                          {isEs ? 'Videos' : 'Videos'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3159" data-mp="dy3fzd527vxb">
                        {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black" data-id="element-3160" data-mp="8za2vltoq6xo" data-mpi={vi}>
                            {video.youtubeId ? <div className="relative w-full" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-3161" data-mp="q7t9187zcch6">
                                <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-3162" data-mp="3tqcpldhaptw5" />
                              </div> : video.facebookUrl ? <a href={video.facebookUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 transition-colors" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-3163" data-mp="2yao9u0diczn0">
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-id="element-3164" data-mp="2kadlb3zo7x50">
                                  <FacebookIcon className="w-8 h-8 text-blue-400" data-id="element-3165" data-mp="2odth5y3x1tgn" />
                                  <span className="font-nunito text-white text-sm font-semibold" data-id="element-3166" data-mp="2tc3b65dwmo0o">
                                    {video.label}
                                  </span>
                                  <span className="font-nunito text-white/50 text-xs" data-id="element-3167" data-mp="8iztyk4jet0j">
                                    {isEs ? 'Ver en Facebook' : 'Watch on Facebook'}
                                  </span>
                                </div>
                              </a> : null}
                            <div className="px-4 py-3 bg-gray-900" data-id="element-3168" data-mp="4wbegnlom3zt">
                              <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-3169" data-mp="3w3xiihcbc8h7">
                                {video.label}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {/* Social Links */}
                {post.socialLinks && post.socialLinks.length > 0 && <div className="mb-8 pt-6 border-t border-gray-100" data-id="element-3170" data-mp="1kbr2qcm391je">
                    <h3 className="font-bebas text-xl text-haa-navy tracking-wider mb-4" data-id="element-3171" data-mp="1gdkybb8q2y1n">
                      {isEs ? 'Síguelos' : 'Follow Them'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-3172" data-mp="3em8moyfq5ooz">
                      {post.socialLinks.map((social, si) => <a key={si} href={social.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito text-sm font-bold transition-all duration-200 ${social.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg hover:shadow-pink-500/25 hover:scale-105' : 'bg-[#1877F2] text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'}`} aria-label={social.label} data-id="element-3173" data-mp="30vvj189v8w5g" data-mpi={si}>
                          {social.platform === 'instagram' ? <InstagramIcon className="w-4 h-4" data-id="element-3174" data-mp="24nfpk0p08j27" /> : <FacebookIcon className="w-4 h-4" data-id="element-3175" data-mp="20mdxc3ti979q" />}
                          {social.label}
                        </a>)}
                    </div>
                  </div>}

                {/* Coverage Links */}
                {post.coverageLinks && post.coverageLinks.length > 0 && <div className="pt-6 border-t border-gray-100" data-id="element-3176" data-mp="1lv529f6u2tp7">
                    <div className="flex items-center gap-2 mb-3" data-id="element-3177" data-mp="1gz6ariq7kvkk">
                      <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-3178" data-mp="1vmrcb87fqmqd" />
                      <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-3179" data-mp="1voua4eetxk5n">
                        {t('news.alsoCoveredBy')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-3180" data-mp="y7n5144sv3eu">
                      {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200" data-id="element-3181" data-mp="1hbbi8g7srg5" data-mpi={i}>
                          {link.label}
                          <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3182" data-mp="p51jo5ut9cz2" />
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// Featured Card
function FeaturedCard({
  post
}: {
  post: BlogPost;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedExcerpt = resolveTranslatedField(t, postNum, 'excerpt', post.excerpt);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const hasStoryUrl = post.storyUrl && post.storyUrl !== '#';
  const hasFullStory = !!post.fullStory || !!post.players || !!post.videoEmbeds;
  const translatedCategory = translateCategory(t, post.category);
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-xl lg:col-span-2 group" data-id="element-3183" data-mp="2xfslaa3ygx0l">
      <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-3184" data-mp="1sc6vcu5pre7h">
        {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition || 'center 25%'
      }} data-id="element-3185" data-mp="1egupbqm3y1b9" /> : !post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && post.videoEmbeds[0].youtubeId ? <iframe src={`https://www.youtube.com/embed/${post.videoEmbeds[0].youtubeId}`} title={post.videoEmbeds[0].label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-3186" data-mp="zpcd83wdcqx6" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3187" data-mp="73noas6s5utk">
            <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 30% 70%, white 1px, transparent 1px)',
          backgroundSize: '25px 25px'
        }} data-id="element-3188" data-mp="26nj0jhr8w3i" />
          </div>}
        {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-3189" data-mp="l12iea7zaqrv" />}
        <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3190" data-mp="uszwboqdfwua">
          <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)} mb-3 inline-block`} data-id="element-3191" data-mp="uti0d7j9zfcr">
            {translatedCategory}
          </span>
          {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <p className="font-bebas text-white text-3xl sm:text-4xl leading-tight tracking-wider line-clamp-2" data-id="element-3192" data-mp="2g4f733kjsfaw">
              {translatedTitle}
            </p>}
        </div>
      </div>

      <div className="p-6 sm:p-8" data-id="element-3193" data-mp="2p64yi9efje8o">
        <div className="flex items-center gap-3 mb-4" data-id="element-3194" data-mp="2p5d5958b1slk">
          <div className="w-8 h-1 bg-haa-blue rounded-full" data-id="element-3195" data-mp="1sg5pkdudadpk" />
          <span className="font-nunito text-haa-blue text-xs font-bold uppercase tracking-widest" data-id="element-3196" data-mp="1sdjc5xksp3zf">
            {t('news.featuredStory')}
          </span>
        </div>

        <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-3" data-id="element-3197" data-mp="21fitys3mwc3f">
          {translatedTitle}
        </h2>

        <div className="mb-6" data-id="element-3198" data-mp="18rmw3k2bwe52">
          <p className="font-nunito text-gray-600 leading-relaxed" data-id="element-3199" data-mp="18seq4qga23t8">
            {translatedExcerpt}
          </p>

          {/* Story preview — show first 2 paragraphs of fullStory */}
          {translatedFullStory && <div className="relative mt-4" data-id="element-3200" data-mp="166oyi73oh00v">
              <div className="font-nunito text-gray-500 text-sm leading-relaxed space-y-3" data-id="element-3201" data-mp="118dn3rxqihgj">
                {stripImageMarkers(translatedFullStory).split('\n\n').slice(0, 2).map((para, i) => <p key={i} data-id="element-3202" data-mp="s6w7sbs5ijsw" data-mpi={i}>{para}</p>)}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-3203" data-mp="n9e8ze7ulkym" />
            </div>}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5" data-id="element-3204" data-mp="dije5lgjdd1k">
            {hasStoryUrl ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-3205" data-mp="8jq5c6c72a7q">
                {t('news.readFullStory')}
                <ExternalLinkIcon className="w-4 h-4" data-id="element-3206" data-mp="2zniiceroa3d6" />
              </a> : hasFullStory ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-3207" data-mp="3ek9bi6ar6iuc">
                {t('news.readFullStory')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-3208" data-mp="2gy3gii45xa7q" />
              </button> : null}

            {post.coverageLinks && post.coverageLinks.length > 0 && <>
                <span className="text-gray-200 select-none hidden sm:inline" data-id="element-3209" data-mp="2qtx2es3rug26">
                  |
                </span>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-3210" data-mp="233t1ishywuev">
                  <NewspaperIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-3211" data-mp="1y72l24293wsx" />
                  <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3212" data-mp="dnqdl5aqbnrk">
                    {t('news.alsoCoveredBy')}
                  </span>
                  {post.coverageLinks.map((link, i) => <Fragment key={link.url} data-id="element-3213" data-mp="3rngiduqifbh" data-mpi={i}>
                      {i > 0 && <span className="text-gray-200 select-none" data-id="element-3214" data-mp="8q6yk367j6yk">·</span>}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3215" data-mp="wfrjgrlx8eah">
                        {link.label}
                        <ExternalLinkIcon className="w-3 h-3" data-id="element-3216" data-mp="3840ytrxclidq" />
                      </a>
                    </Fragment>)}
                </div>
              </>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-3217" data-mp="1ilu2pal6gqkc">
          <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm" data-id="element-3218" data-mp="1dnsi2l83sj6r">
            <span className="flex items-center gap-1.5" data-id="element-3219" data-mp="18x2b53k1489y">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-3220" data-mp="18vizxrd8cnf1" />
              <span className="font-nunito" data-id="element-3221" data-mp="13x7o5b3dk2u6">{translatedDate}</span>
            </span>
          </div>
          <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1.5 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3222" data-mp="zxp1oavwnjp8">
            <Share2Icon className="w-3.5 h-3.5" data-id="element-3223" data-mp="kzwmxky6mzc3" />
            {isEs ? 'Compartir' : 'Share'}
          </a>
        </div>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3224" data-mp="2kb7hu4bmmw8z" />
    </motion.article>;
}
// Regular Card
function PostCard({
  post,
  index
}: {
  post: BlogPost;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedExcerpt = resolveTranslatedField(t, postNum, 'excerpt', post.excerpt);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedReadTime = resolveTranslatedField(t, postNum, 'readTime', post.readTime);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const translatedCategory = translateCategory(t, post.category);
  const isSportsComposite = post.id === 'post-8' || post.players && post.players.length > 0;
  const isWabafu = post.id === 'post-12';
  const compositeImages = SPORTS_COMPOSITE_IMAGES;
  const hasMultipleImages = !!post.secondImage;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col" data-id="element-3225" data-mp="1b2lh20lwfsze">
      {isSportsComposite ? <div className="relative h-64 overflow-hidden flex-shrink-0 bg-gradient-to-b from-haa-midblue via-haa-blue to-haa-navy" data-id="element-3226" data-mp="28r59g4an6pwq">
          {/* Player Images */}
          <div className="absolute inset-0 flex items-end justify-center px-2 pb-12 sm:pb-10" data-id="element-3227" data-mp="23u53dtdsln5m">
            {compositeImages.map((img, i) => <motion.div key={img.name} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + i * 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-1/5 -mx-1.5 sm:-mx-2" data-id="element-3228" data-mp="2scx4mzyzhwyf" data-mpi={i}>
                <img src={img.url} alt={img.name} className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-3229" data-mp="1znzs7dhgeudl" />
              </motion.div>)}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-haa-navy/80 to-transparent" data-id="element-3230" data-mp="1ps6cxmccoms4" />
          {/* Top Left Badge */}
          <div className="absolute top-3 left-3" data-id="element-3231" data-mp="3nl8z6b1c26mq">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3232" data-mp="3in77j1pucyzn">
              {translatedCategory}
            </span>
          </div>
          {/* Bottom Text */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pb-3" data-id="element-3233" data-mp="f5pbrsncfifl">
            <p className="text-white/90 font-nunito text-[10px] sm:text-xs font-bold tracking-widest mb-1 flex items-center gap-1.5" data-id="element-3234" data-mp="a8fg3v9d2ajb">
              <span className="text-lg leading-none" data-id="element-3235" data-mp="5adx1abxqb7u">🇭🇳</span>
              {isEs ? 'HONDUREÑOS EN LA MLS · TEMPORADA 2025-26' : 'HONDURANS IN MLS · 2025-26 SEASON'}
            </p>
          </div>
        </div> : isWabafu ? <div className="relative h-52 overflow-hidden flex-shrink-0" data-id="element-3236" data-mp="2lw5af5zuca4b">
          <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center center'
      }} data-id="element-3237" data-mp="11ffm744ko510" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-3238" data-mp="1f6u7jhlycib8" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-3239" data-mp="1k66rnulfvba4">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={inView ? {
          scale: 1,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3240" data-mp="1p3p3bsquc4lt">
              <span className="text-5xl mb-3 block" data-id="element-3241" data-mp="3t89t82118e3">🥁</span>
              <p className="font-bebas text-white text-2xl sm:text-3xl tracking-wider leading-tight" data-id="element-3242" data-mp="25nd0qt3p3wi2">
                {isEs ? 'CULTURA EN MOVIMIENTO' : 'CULTURE IN MOTION'}
              </p>
              <p className="font-nunito text-white/70 text-xs tracking-widest uppercase mt-1" data-id="element-3243" data-mp="2fdfq8jblysbn">
                Wabafu Garifuna Dance Theater
              </p>
            </motion.div>
          </div>
          <div className="absolute top-3 left-3" data-id="element-3244" data-mp="1mqv01m6vcm9o">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3245" data-mp="1mrmwt0c7cq1j">
              {translatedCategory}
            </span>
          </div>
        </div> : hasMultipleImages ? <div className="flex-shrink-0" data-id="element-3246" data-mp="1rqzihhrc7r2j">
          <div className="grid grid-cols-2 gap-0.5" data-id="element-3247" data-mp="382mywb4s0yam">
            <div className="relative h-36 overflow-hidden" data-id="element-3248" data-mp="38597me3eljuf">
              <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: post.imagePosition ?? 'center 15%'
          }} data-id="element-3249" data-mp="3330bx9z5c3om" />
            </div>
            <div className="relative h-36 overflow-hidden" data-id="element-3250" data-mp="fxyv0xu6yxta">
              <img src={post.secondImage!} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: 'center center'
          }} data-id="element-3251" data-mp="kvi83uf5l4jn" />
            </div>
          </div>
          <div className="relative -mt-8 px-3 pb-0 z-10" data-id="element-3252" data-mp="13tl1sminbpns">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3253" data-mp="13s09z4npuktm">
              {translatedCategory}
            </span>
          </div>
        </div> : <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-3254" data-mp="1m2zgjh02ow6m">
          {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center 15%'
      }} data-id="element-3255" data-mp="1r2lujsnq6ggh" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3256" data-mp="24ua69uu2b2wx">
              <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-3257" data-mp="29uo46k1y8wh8" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-3258" data-mp="2sjurkkdouc9d" />
          <div className="absolute top-3 left-3" data-id="element-3259" data-mp="h42nsjlypjzx">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3260" data-mp="h2riuggs6678">
              {translatedCategory}
            </span>
          </div>
        </div>}

      <div className="p-5 flex flex-col flex-1" data-id="element-3261" data-mp="lusp9lr9p2pc">
        <div className="flex items-center gap-2 mb-2" data-id="element-3262" data-mp="lx5gkpix0pf2">
          <div className="w-4 h-0.5 bg-haa-blue rounded-full" data-id="element-3263" data-mp="3ubxac2mvbpyk" />
          <span className="font-nunito text-gray-400 text-xs" data-id="element-3264" data-mp="3pbsumetsaz9e">
            {translatedDate}
          </span>
        </div>

        {!isSportsComposite && <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight mb-2" data-id="element-3265" data-mp="858rkucjmvmw">
            {translatedTitle}
          </h3>}

        <div className="flex-1 mb-3" data-id="element-3266" data-mp="1jikr4t19ss39">
          {!isSportsComposite && <p className="font-nunito text-gray-500 text-sm leading-relaxed line-clamp-3" data-id="element-3267" data-mp="1jknojy4r5ni0">
              {translatedExcerpt}
            </p>}
        </div>

        <div className="mb-4" data-id="element-3268" data-mp="1lm4xhboc7y4e">
          {post.storyUrl && post.storyUrl !== '#' ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3269" data-mp="1qkzkkptc88ub">
              {t('news.readFullStory')}
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3270" data-mp="17wb88s8x7bu5" />
            </a> : post.fullStory || post.players || post.videoEmbeds ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3271" data-mp="1csrwrnk17s7r">
              {t('news.readFullStory')}
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3272" data-mp="32e0cpn1fhq9w" />
            </button> : null}
        </div>

        {post.coverageLinks && post.coverageLinks.length > 0 && <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3" data-id="element-3273" data-mp="2tdu1y8x474ef">
            <NewspaperIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-3274" data-mp="2ofsj9p3lp73h" />
            <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3275" data-mp="2ohmweh8mrux0">
              {t('news.alsoCoveredBy')}
            </span>
            {post.coverageLinks.map((link, i) => <Fragment key={i} data-id="element-3276" data-mp="2ao5awqwdw84c" data-mpi={i}>
                {i > 0 && <span className="text-gray-200 select-none" data-id="element-3277" data-mp="2aox6i1pze5um">·</span>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3278" data-mp="25pkktkauj4tm">
                  {link.label}
                  <ExternalLinkIcon className="w-3 h-3" data-id="element-3279" data-mp="3vtmfetj54xco" />
                </a>
              </Fragment>)}
          </div>}
      </div>

      <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100 mt-auto" data-id="element-3280" data-mp="3m3kprxb536vn">
        <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs" data-id="element-3281" data-mp="3r1w0ea99dlex">
          <span className="flex items-center gap-1" data-id="element-3282" data-mp="3d2movnvzzfha">
            <CalendarIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-3283" data-mp="3d11rzrm7nkg9" />
            <span className="font-nunito" data-id="element-3284" data-mp="3i1zjwvx0vsuy">{translatedDate}</span>
          </span>
        </div>
        <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3285" data-mp="334yp2jjjbhs9">
          <Share2Icon className="w-3.5 h-3.5" data-id="element-3286" data-mp="11b99m2e521t6" />
          {isEs ? 'Compartir' : 'Share'}
        </a>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3287" data-mp="2qty01ateuxru" />
    </motion.article>;
}
// Main Section
export function NewsBlog() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    t
  } = useLanguage();
  const filtered = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured) ?? null;
  const rest = filtered.filter(p => p.id !== featured?.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const categoryTranslations: Record<string, string> = {
    All: t('news.catAll'),
    Political: t('news.catPolitical'),
    Immigration: t('news.catImmigration'),
    Business: t('news.catBusiness'),
    'Note Worthy': t('news.catNoteWorthy'),
    Events: t('news.catEvents'),
    'Arts & Entertainment': t('news.catArtMedia'),
    Sports: t('news.catSports')
  };
  return <section id="news" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="news-heading" data-id="element-3288" data-mp="3tat4s4go8v6j">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3289" data-mp="71i5w85e4d11">
        <div ref={titleRef} className="mb-10" data-id="element-3290" data-mp="70yuzbe5qowi">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-3291" data-mp="c0bky50bsc3b">
            <div data-id="element-3292" data-mp="urceueml9hg0">
              <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3293" data-mp="uoq82h7iv5yu">
                {t('news.label')}
              </span>
              <h2 id="news-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3294" data-mp="zubvri159m5v">
                {t('news.heading')}
              </h2>
              <div className="flex items-center gap-3 mt-4" data-id="element-3295" data-mp="1nlhlcowvzstd">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3296" data-mp="1nkzjncrggme2" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3297" data-mp="1im4p5dk4l9e4" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3298" data-mp="1dvo87jh3j5n0" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by category" data-id="element-3299" data-mp="19tb8bbp161y5">
          {ALL_CATEGORIES.map(cat => <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => setActiveCategory(cat)} className={`relative font-nunito font-semibold text-sm px-5 py-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${activeCategory === cat ? 'bg-haa-navy text-white shadow-md' : 'bg-white text-gray-500 hover:text-haa-navy border border-gray-200 hover:border-haa-navy'}`} data-id="element-3300" data-mp="2bcy1qgnaqe8x">
              <span className="inline-flex items-center gap-1.5" data-id="element-3301" data-mp="1imphmj979jrp">
                {cat === 'Sports' && <TrophyIcon className="w-3.5 h-3.5" data-id="element-3302" data-mp="1nlivicsefcse" />}
                {categoryTranslations[cat] ?? cat}
              </span>
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-3303" data-mp="5q0cz8ua4m2s">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-3304" data-mp="pcc7vg90qvd">
            {filtered.length === 0 ? <div className="text-center py-20" data-id="element-3305" data-mp="3e50igaimclvw">
                <TagIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-3306" data-mp="3rv40sqednvg2" />
                <p className="font-nunito text-gray-400 text-lg" data-id="element-3307" data-mp="3rx8cvvmfk0rf">
                  {t('news.noPosts')}
                </p>
              </div> : activeCategory === 'All' ? <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3308" data-mp="2zcgj7kylayjq">
                  {featured && <FeaturedCard post={featured} data-id="element-3309" data-mp="2vczeitqzm5fm" />}
                  {rest.length > 0 && <div className="flex flex-col gap-6" data-id="element-3310" data-mp="1sh0jytva74m1">
                      {rest.slice(0, 2).map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3311" data-mp="1nn63s0evx7rp" data-mpi={i} />)}
                    </div>}
                </div>
                {rest.length > 2 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-id="element-3312" data-mp="1emhpr5phr7et">
                    {rest.slice(2).map((post, i) => <PostCard key={post.id} post={post} index={i + 2} data-id="element-3313" data-mp="27e1e140y3bno" data-mpi={i} />)}
                  </div>}
              </> : <div className={`grid gap-6 ${filtered.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`} data-id="element-3314" data-mp="1xf2bm36rwc94">
                {filtered.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3315" data-mp="lzdq7g963laf" data-mpi={i} />)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}